import React, { useEffect, useRef, useState } from "react";

import {
  INSTRUCTION_EXAMPLES,
  INSTRUCTIONS_MAX,
  instructionsProblem,
} from "./support.utils";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

/**
 * "Write with instructions" (owner 2026-10-27, Loom 4b22d6ca): the owner tells the AI what the reply must say, e.g.
 * "tell him we'll fix it within 24 hours", and gets a draft to check. Used on a case and on the Emails tab.
 */
export const WriteWithInstructions = ({
  busy,
  note,
  onWrite,
  onCancel,
}: {
  busy: boolean;
  /** One line under the box (what happens when they click Write draft). */
  note?: React.ReactNode;
  onWrite: (instructions: string) => void;
  onCancel: () => void;
}) => {
  const [text, setText] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => box.current?.focus(), []);
  const problem = instructionsProblem(text);
  const submit = () => {
    if (!problem && !busy) onWrite(text.trim());
  };
  return (
    <div
      className={
        "mb-3 rounded-xl border-[1.5px] border-[#6B4EFF] bg-[#FBFAFF] px-4 py-3"
      }
      data-testid={"write-instructions"}>
      <label
        htmlFor={"support-instructions"}
        className={"mb-1.5 block text-[12.5px] text-[#4B32D6]"}>
        <b>✍️ What should the reply say?</b>{" "}
        <span className={"text-[#7A7F8C]"}>
          The AI writes it for you to check. Nothing is sent.
        </span>
      </label>
      <textarea
        id={"support-instructions"}
        ref={box}
        value={text}
        rows={3}
        maxLength={INSTRUCTIONS_MAX + 200}
        placeholder={
          "e.g. Tell him sorry about the missed meeting and that we'll fix it within 24 hours"
        }
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") onCancel();
        }}
        className={
          "w-full resize-y rounded-lg border border-[#DCE2EA] bg-white px-3 py-2 text-[14px] leading-relaxed text-[#1F2433] outline-none focus:border-[#6B4EFF]"
        }
      />
      <div className={"mt-1.5 flex flex-wrap gap-1.5"}>
        {INSTRUCTION_EXAMPLES.map((example) => (
          <button
            key={example}
            type={"button"}
            onClick={() => {
              setText(example);
              box.current?.focus();
            }}
            className={
              "rounded-full bg-[#F1F2F5] px-2.5 py-1 text-[12px] text-[#3A4256] hover:text-[#6B4EFF]"
            }>
            {example}
          </button>
        ))}
      </div>
      <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
        <button
          type={"button"}
          disabled={!!problem || busy}
          title={text.trim() ? problem || undefined : undefined}
          onClick={submit}
          className={PRIMARY_BUTTON}
          data-testid={"write-instructions-go"}>
          {busy ? "Writing…" : "Write draft"}
        </button>
        <button
          type={"button"}
          onClick={onCancel}
          disabled={busy}
          className={SMALL_BUTTON}>
          Cancel
        </button>
        {text.trim().length > INSTRUCTIONS_MAX && (
          <span className={"text-[12.5px] text-[#9A5B00]"}>{problem}</span>
        )}
      </div>
      {note && (
        <div className={"mt-2 text-[12.5px] text-[#5D6475]"}>{note}</div>
      )}
    </div>
  );
};
